import React, { useCallback, useRef, useState } from 'react';
import {
  useImageSlots,
  uploadImageFile,
  createImageSlot,
  isGallerySlot,
} from '../lib/image-slots-client';
import { Upload, Check, ClipboardCheck, RefreshCw } from '../lib/icons';

export default function MediaLibraryPanel() {
  const { slots, loading, refetch } = useImageSlots();
  const [uploading, setUploading] = useState(false);
  const [lastUploaded, setLastUploaded] = useState<{ slotId: string; url: string } | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // All slots with images, excluding gallery child slots, newest first
  const mediaSlots = slots
    .filter(s => s.image_url && !isGallerySlot(s.slot_id))
    .sort((a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime());

  const handleFiles = useCallback(
    async (files: FileList | null) => {
      if (!files?.length) return;
      const file = files[0];
      setUploading(true);
      setUploadError(null);
      setLastUploaded(null);
      try {
        const slotId = `media-${Date.now()}`;
        const url = await uploadImageFile(slotId, file);
        await createImageSlot(
          slotId,
          url,
          file.name.replace(/\.[^/.]+$/, ''),
          'media-library',
          `Upload: ${file.name}`,
        );
        setLastUploaded({ slotId, url });
        refetch();
      } catch (e) {
        setUploadError(e instanceof Error ? e.message : String(e));
      } finally {
        setUploading(false);
      }
    },
    [refetch],
  );

  const copy = async (text: string, id: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId(null), 2000);
    } catch {
      /* clipboard unavailable */
    }
  };

  return (
    <div className="min-h-screen bg-[#0D0D0D] p-6 text-[#E0E0E0]">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-2">
          <h2 className="text-xl font-bold">Media Library</h2>
          <button
            onClick={() => refetch()}
            className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-[#E0E0E0] transition-colors"
          >
            <RefreshCw className="h-3.5 w-3.5" /> Refresh
          </button>
        </div>
        <p className="text-sm text-[#B0B0B0] mb-8">
          Upload photos here. After uploading, copy the Slot ID and ask Claude to assign it to a
          specific image area on the site — no more copying filenames or dropping files in /public/.
        </p>

        {/* Upload area */}
        <div
          className={`border-2 border-dashed rounded-2xl p-10 text-center mb-6 transition-all cursor-pointer ${
            dragOver
              ? 'border-[#39FF14] bg-[#39FF14]/5'
              : 'border-gray-700 hover:border-gray-600 hover:bg-gray-900/30'
          } ${uploading ? 'opacity-60 pointer-events-none' : ''}`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={e => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={e => {
            e.preventDefault();
            setDragOver(false);
            handleFiles(e.dataTransfer.files);
          }}
        >
          <Upload className="h-10 w-10 text-gray-600 mx-auto mb-3" />
          {uploading ? (
            <p className="text-[#39FF14] font-semibold animate-pulse">Uploading to Supabase…</p>
          ) : (
            <>
              <p className="text-[#E0E0E0] font-semibold mb-1">
                Drop an image here or click to browse
              </p>
              <p className="text-xs text-[#B0B0B0]">JPG, PNG, WebP · max 8 MB</p>
            </>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={e => {
              handleFiles(e.target.files);
              e.target.value = '';
            }}
          />
        </div>

        {uploadError && (
          <div className="bg-red-900/30 border border-red-700 rounded-xl p-4 mb-6 text-red-400 text-sm">
            {uploadError}
          </div>
        )}

        {lastUploaded && (
          <div className="bg-[#39FF14]/5 border border-[#39FF14]/30 rounded-xl p-5 mb-8">
            <div className="flex items-start gap-3">
              <Check className="h-5 w-5 text-[#39FF14] shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="text-[#39FF14] font-semibold text-sm mb-3">Uploaded successfully!</p>
                <img
                  src={lastUploaded.url}
                  alt="Just uploaded"
                  className="w-32 h-20 object-cover rounded-lg mb-3"
                />
                <p className="text-xs text-[#B0B0B0] mb-1">Slot ID (tell Claude to assign this):</p>
                <div className="flex items-center gap-2">
                  <code className="text-xs bg-gray-900 border border-gray-700 px-3 py-2 rounded-lg text-[#E0E0E0] flex-1 overflow-x-auto whitespace-nowrap">
                    {lastUploaded.slotId}
                  </code>
                  <button
                    onClick={() => copy(lastUploaded.slotId, '__last__')}
                    className="shrink-0 flex items-center gap-1.5 bg-gray-800 hover:bg-gray-700 border border-gray-700 text-xs font-semibold px-3 py-2 rounded-lg transition-colors"
                  >
                    {copiedId === '__last__' ? (
                      <><ClipboardCheck className="h-3.5 w-3.5 text-[#39FF14]" /> Copied!</>
                    ) : (
                      'Copy ID'
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Image grid */}
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-[#B0B0B0] uppercase tracking-widest">
            All Site Images ({mediaSlots.length})
          </h3>
        </div>

        {loading ? (
          <p className="text-[#B0B0B0] text-sm">Loading…</p>
        ) : mediaSlots.length === 0 ? (
          <p className="text-[#B0B0B0] text-sm">No images uploaded yet — try uploading one above.</p>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {mediaSlots.map(slot => (
              <div
                key={slot.slot_id}
                className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden group"
              >
                <div className="aspect-square overflow-hidden">
                  <img
                    src={slot.image_url!}
                    alt={slot.alt_text || slot.slot_id}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                  />
                </div>
                <div className="p-2.5">
                  <p
                    className="text-[10px] text-[#B0B0B0] truncate mb-1.5 font-mono"
                    title={slot.slot_id}
                  >
                    {slot.slot_id}
                  </p>
                  {slot.label && slot.label !== slot.slot_id && (
                    <p className="text-[10px] text-gray-600 truncate mb-1.5" title={slot.label}>
                      {slot.label}
                    </p>
                  )}
                  <button
                    onClick={() => copy(slot.slot_id, slot.slot_id)}
                    className="flex items-center gap-1 text-[10px] font-semibold transition-colors text-[#39FF14] hover:text-white"
                  >
                    {copiedId === slot.slot_id ? (
                      <><ClipboardCheck className="h-3 w-3" /> Copied!</>
                    ) : (
                      'Copy Slot ID'
                    )}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
